// Which ingredients a method step uses, for the chips in cooking mode. Not
// stored: imported steps are prose, so it's inferred by looking for the
// ingredient's words among the step's, with the same keys the list uses
// (normalize.ts), so "tomatoes" finds "Cherry tomatoes" and "onion" "gul lök".
//
// A word of the name counts when it says which ingredient: its head ("tomato"),
// unless other names use that word as a modifier ("light" in "Light Käse" and
// "Light Mayonnaise"), or any other word no other ingredient shares ("cherry").
// German and Swedish write compounds, so a word of four letters or more also
// counts inside a step's word ("Zwiebelwürfel": Zwiebel) or at the end of the
// name's ("Blattspinat": Spinat) — never when the step's word is itself an
// ingredient's ("Paprikapulver" is the spice, not the pepper). Kept local:
// normalize.ts is the list's identity and stays as strict as it is.

import { fold, nameKey } from './normalize'

/** Shorter words are never looked for inside others: "ei" is in "Eintopf". */
const MIN_INSIDE = 4

const keyWords = (s: string) => nameKey(s).split(' ').filter(w => /[\p{L}\p{N}]/u.test(w))

// "Eiern", "Zwiebeln": German's dative plural, which the list's stemmer keeps.
const stepKeys = (word: string) => {
  const keys = keyWords(word)
  return /\p{L}{3,}n$/u.test(word) ? [...keys, ...keyWords(word.slice(0, -1))] : keys
}

export function stepUses(text: string, ingredientNames: string[]): number[] {
  const raw = text.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  const perWord = raw.map(stepKeys)
  const words = new Set(perWord.flat())

  const names = ingredientNames.map(keyWords)
  const count = new Map<string, number>()
  for (const ws of names) for (const w of new Set(ws)) count.set(w, (count.get(w) ?? 0) + 1)
  const modifiers = new Set(names.flatMap(ws => ws.slice(0, -1)))
  const known = new Set(names.flat())
  // The step's words that aren't an ingredient's own word, to look inside.
  const compounds = raw.filter((_, k) => !perWord[k].some(w => known.has(w))).map(w => fold(w))

  const out: number[] = []
  names.forEach((ws, i) => {
    const head = ws[ws.length - 1]
    if (!head) return
    const telling = ws.filter(w => count.get(w) === 1 || (w === head && !modifiers.has(w)))
    const found = telling.some(w => words.has(w)) || telling.some(w =>
      w.length >= MIN_INSIDE && compounds.some(c => c.includes(w) || (c.length >= MIN_INSIDE && w.endsWith(c))))
    if (found) out.push(i)
  })
  return out
}

/**
 * A step's text in parts, the words that name one of its ingredients marked —
 * cooking mode underlines them. The same matching as stepUses, word by word,
 * so a marked word is always one the panel lists under "This step".
 */
export function stepParts(text: string, ingredientNames: string[]): { text: string; mark: boolean }[] {
  const uses = new Set(stepUses(text, ingredientNames))
  if (!uses.size) return [{ text, mark: false }]
  const out: { text: string; mark: boolean }[] = []
  for (const piece of text.split(/([\p{L}\p{N}]+)/u)) {
    if (!piece) continue
    const mark = /[\p{L}\p{N}]/u.test(piece) && stepUses(piece, ingredientNames).some(i => uses.has(i))
    const prev = out[out.length - 1]
    if (prev && prev.mark === mark) prev.text += piece
    else out.push({ text: piece, mark })
  }
  return out
}
